
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import ProfileEditor from '@/components/account/ProfileEditor';
import SecuritySettings from '@/components/account/SecuritySettings';
import { useToast } from '@/hooks/use-toast';
import { CustomerProfile } from '@/types/profile';
import { fetchUserProfile } from '@/services/profileService';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AppLoader from '@/components/common/AppLoader';

const AccountSettingsPage: React.FC = () => {
  const { t } = useTranslation(['common', 'account', 'profile', 'navigation']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Optimized function to load the profile
  const loadProfile = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setFetchError(null);
      
      const { profile, error } = await fetchUserProfile(user.id);
      
      if (error) {
        console.error('Erreur lors du chargement du profil:', error);
        setFetchError(t('profile:errorFetchingProfile'));
        toast({
          title: t('profile:error'),
          description: t('profile:errorFetchingProfile'),
          variant: 'destructive',
        });
        return;
      }
      
      if (profile) {
        setProfile(profile);
      }
    } catch (error) {
      console.error('Exception lors du chargement du profil:', error);
      setFetchError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [user, t, toast]);

  // Load the profile when the component mounts
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadProfile();
  }, [user, navigate, loadProfile]);

  // Optimized handler for navigating back
  const handleGoBack = useCallback(() => {
    navigate(-1); // This navigates to the previous page in history
  }, [navigate]);

  // Optimized handler for profile updates
  const handleUpdateSuccess = useCallback((updatedProfile: CustomerProfile) => {
    // Update profile directly without reloading
    setProfile(updatedProfile);
    setIsSaving(false);
  }, []);

  if (!user) {
    return null; // The redirection will be handled by the useEffect
  }

  if (loading) {
    return <AppLoader size="medium" message={t('navigation:loading', 'Loading account settings...')} />;
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleGoBack}
            className="mr-4 flex items-center text-muted-foreground hover:text-foreground"
            aria-label={t('navigation:goBack')}
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            {t('navigation:goBack')}
          </Button>
          <h1 className="text-3xl font-bold">{t('account:settings')}</h1>
        </div>
        
        {fetchError && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {t('profile:errorFetchingProfile')}: {fetchError}
            <button 
              onClick={loadProfile} 
              className="ml-4 text-blue-600 underline"
              disabled={loading}
            >
              {loading ? t('common:loading') : t('common:retry')}
            </button>
          </div>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="profile">{t('account:profileSettings')}</TabsTrigger>
            <TabsTrigger value="security">{t('account:securitySettings')}</TabsTrigger>
            <TabsTrigger value="preferences">{t('account:preferences')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="profile" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('account:profileInformation')}</CardTitle>
                <CardDescription>
                  {t('account:updateYourProfile')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {profile ? (
                  <ProfileEditor 
                    profile={profile} 
                    onUpdateSuccess={handleUpdateSuccess}
                    isGlobalSaving={isSaving}
                    setGlobalSaving={setIsSaving}
                  />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t('profile:noProfileData')}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="security" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('account:securitySettings')}</CardTitle>
                <CardDescription>
                  {t('account:manageYourSecurity')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SecuritySettings />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="preferences" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('account:preferences')}</CardTitle>
                <CardDescription>
                  {t('account:customizeYourExperience')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{t('account:comingSoon')}</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AccountSettingsPage;
